import React from 'react';
import {
  ArrowRightIcon,
  BookOpenCheckIcon,
  Building2Icon,
  CircleCheckIcon,
  ScaleIcon,
  TimerResetIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { BrandMark } from './BrandMark';
import { ComplianceVisual } from './ComplianceVisual';
import { DriverEcosystem } from './DriverEcosystem';
import { Faq } from './Faq';
import { LandingHeader } from './LandingHeader';
import { StakeholderExperience } from './StakeholderExperience';

type LandingPageProps = {
  onLogin: () => void;
  onRegister: () => void;
};

type ProofItem = {
  value: string;
  label: string;
  icon: typeof TimerResetIcon;
};

const proof: ProofItem[] = [
  {
    value: '24 h',
    label: 'Vi räknar din vila i ett rullande dygn på 24 timmar.',
    icon: TimerResetIcon,
  },
  {
    value: '11 h',
    label: 'Du behöver minst 11 timmars dygnsvila varje dygn.',
    icon: CircleCheckIcon,
  },
  {
    value: '1 tidbok',
    label: 'En personlig tidbok per förare.',
    icon: BookOpenCheckIcon,
  },
  {
    value: 'Vid kontroll',
    label: 'Visa tidboken för polis eller bilinspektör.',
    icon: ScaleIcon,
  },
];

const preShiftSteps = [
  ['01', 'Se din vila', 'Kontrollera att du har vilat tillräckligt.'],
  ['02', 'Se dagens läge', 'Körpass, raster och anslutna appar på ett ställe.'],
  ['03', 'Ha tidboken redo', 'Klar att visa vid en kontroll.'],
];

const authorityContexts = [
  { name: 'Transportstyrelsen', focus: 'Taxi och transportregler', logo: '/Swedish_Transport_Agency-Logo.wine.png' },
  { name: 'Skatteverket', focus: 'Företagande och redovisning', logo: '/Skatteverket_vertical.jpg' },
  { name: 'Migrationsverket', focus: 'Rätt att arbeta i Sverige', logo: '/migrationsverket-logo-8E675F3D5C-seeklogo.com-2129041551.png' },
  { name: 'Polismyndigheten', focus: 'Kontroll och trafiksäkerhet', logo: '/polisen_twitter_image-508163412.png' },
  { name: 'Arbetsförmedlingen', focus: 'Arbetsmarknad och stöd', logo: '/arbetsformedlingen-logo-1-3145632998.png' },
];

const ease = [0.22, 1, 0.36, 1] as const;
const reveal = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, ease },
  },
};

export function LandingPage({ onLogin, onRegister }: LandingPageProps) {
  return (
    <div className="ivilotid-site min-h-screen bg-[#F4F8FA] text-[#0B1120]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-[#0B1120] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Hoppa till innehåll
      </a>
      <LandingHeader onLogin={onLogin} onRegister={onRegister} />
      <main id="main-content" tabIndex={-1}>
        <section id="top" className="border-b border-[#0B1120]/10">
          <div className="mx-auto grid max-w-6xl gap-9 px-5 py-12 sm:px-6 sm:py-16 lg:grid-cols-[1fr_.9fr] lg:items-center lg:gap-14 lg:px-8">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.06 } },
              }}
              className="min-w-0"
            >
              <motion.p
                variants={reveal}
                className="inline-flex items-center gap-2 rounded-md border border-[#06B6D4]/30 bg-white px-2.5 py-1 text-xs font-bold tracking-[.05em] text-[#006D98]"
              >
                <ScaleIcon size={13} aria-hidden="true" />
                Förordning (1994:1297) · TSFS 2015:17
              </motion.p>
              <motion.p
                variants={reveal}
                className="mt-5 text-xs font-bold uppercase tracking-[.12em] text-[#006D98]"
              >
                Digital vilotid för svensk taxi
              </motion.p>
              <motion.h1
                variants={reveal}
                className="mt-3 max-w-2xl font-serif-display text-[clamp(3rem,6vw,4.6rem)] font-medium leading-[.96] tracking-[-.035em]"
              >
                Vet när du får köra.
                <span className="mt-1 block italic font-normal text-[#008FC2]">
                  Och när du måste vila.
                </span>
              </motion.h1>
              <motion.p
                variants={reveal}
                className="mt-5 max-w-xl text-base leading-7 text-slate-700"
              >
                iVilotid samlar dina körpass, dina raster och din vilotid på ett
                ställe. Appen räknar din dygnsvila enligt svensk lag — så att du
                vet att du får köra innan du börjar passet.
              </motion.p>
              <motion.div variants={reveal} className="mt-6 flex flex-col gap-2.5 sm:flex-row">
                <motion.button
                  type="button"
                  onClick={onLogin}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.14 }}
                  className="group inline-flex min-h-11 items-center justify-center gap-2.5 rounded-lg bg-[#06B6D4] px-5 text-sm font-bold text-[#071A28] shadow-[0_6px_14px_rgba(6,182,212,.18)] hover:bg-[#22D3EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007EAF] focus-visible:ring-offset-2"
                >
                  Starta ditt körpass
                  <ArrowRightIcon
                    size={17}
                    className="transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </motion.button>
                <motion.button
                  type="button"
                  onClick={onRegister}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.14 }}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#0B1120]/15 bg-white px-5 text-sm font-semibold hover:border-[#06B6D4] hover:text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-2"
                >
                  <Building2Icon size={17} aria-hidden="true" />
                  Registrera taxiföretag
                </motion.button>
              </motion.div>
              <motion.p variants={reveal} className="mt-4 max-w-lg text-sm leading-6 text-slate-700">
                iVilotid är ett stöd. Tjänsten är inte ett juridiskt beslut och
                tar inte bort ditt eget ansvar enligt lagen.
              </motion.p>
            </motion.div>
            <div className="min-w-0">
              <ComplianceVisual />
            </div>
          </div>
        </section>

        <section id="plattformen" aria-label="Grund för kontroll" className="bg-white py-12 sm:py-14">
          <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">
                  En gemensam grund
                </p>
                <h2 className="mt-2 font-display text-2xl tracking-[-.035em] sm:text-3xl">
                  Ett tryggt körpass börjar med rätt vila.
                </h2>
              </div>
              <p className="max-w-sm text-sm leading-6 text-slate-700">
                Samma tydliga information för föraren, företaget och
                myndigheten.
              </p>
            </div>
            <div className="mt-6 grid overflow-hidden rounded-xl border border-[#0B1120]/10 sm:grid-cols-2 lg:grid-cols-4">
              {proof.map(({ value, label, icon: Icon }, index) => (
                <motion.article
                  key={value}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.28, delay: index * 0.04, ease }}
                  className="min-w-0 border-b border-[#0B1120]/10 p-4 last:border-b-0 sm:[&:nth-child(odd)]:border-r sm:[&:nth-last-child(-n+2)]:border-b-0 lg:border-b-0 lg:border-r lg:last:border-r-0"
                >
                  <Icon size={17} className="text-[#008FC2]" aria-hidden="true" />
                  <p className="mt-4 font-display text-2xl tracking-[-.04em]">{value}</p>
                  <p className="mt-1.5 text-sm leading-6 text-slate-700">{label}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="pre-shift-heading" className="bg-[#EAF5F8] py-14 sm:py-16">
          <div className="mx-auto grid max-w-6xl gap-8 px-5 sm:px-6 lg:grid-cols-[.85fr_1.15fr] lg:gap-12 lg:px-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">
                Före första uppdraget
              </p>
              <h2
                id="pre-shift-heading"
                className="mt-3 font-display text-3xl leading-[1.08] tracking-[-.04em] sm:text-4xl"
              >
                Vet att allt stämmer innan du kör.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-slate-700">
                Slipp hoppa mellan olika system. iVilotid samlar allt du behöver
                se och dokumentera på ett och samma ställe.
              </p>
            </div>
            <ol className="grid gap-2.5">
              {preShiftSteps.map(([number, title, copy], index) => (
                <motion.li
                  key={number}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.28, delay: index * 0.04, ease }}
                  className="grid grid-cols-[34px_1fr] gap-3 rounded-xl border border-[#0B1120]/10 bg-[#F8FCFD] p-3.5"
                >
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0B1120] text-xs font-bold text-[#CFFAFE]">
                    {number}
                  </span>
                  <div>
                    <h3 className="text-sm font-bold">{title}</h3>
                    <p className="mt-0.5 text-sm leading-6 text-slate-700">{copy}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        <StakeholderExperience />
        <DriverEcosystem />
        <Faq />

        <section id="lag-kontroll" className="bg-[#EAF5F8] py-14 sm:py-16">
          <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
            <div className="rounded-2xl border border-[#06B6D4]/20 bg-[#F5FBFC] p-5 sm:p-6">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex max-w-2xl gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#F5C400] text-[#0B1120]">
                    <ScaleIcon size={18} aria-hidden="true" />
                  </span>
                  <div>
                    <h2 className="font-display text-2xl tracking-[-.03em]">
                      Redo för kontroll.
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-700">
                      Byggd enligt Förordning (1994:1297) om vilotider och
                      TSFS 2015:17 om personlig tidbok. Tjänsten är ett stöd —
                      inte ett juridiskt beslut.
                    </p>
                  </div>
                </div>
                <motion.button
                  type="button"
                  onClick={onLogin}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-[#0B1120]/15 bg-white px-4 text-sm font-semibold hover:border-[#06B6D4] hover:text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
                >
                  Till förarstarten
                  <ArrowRightIcon size={16} aria-hidden="true" />
                </motion.button>
              </div>
              <section aria-labelledby="authority-context-heading" className="mt-5 border-t border-[#0B1120]/10 pt-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[.1em] text-[#006D98]">
                      Myndigheter
                    </p>
                    <h3 id="authority-context-heading" className="mt-1 font-display text-lg tracking-[-.02em]">
                      En tryggare taxibransch bygger på tydligt ansvar.
                    </h3>
                  </div>
                  <p className="max-w-sm text-sm leading-6 text-slate-700">
                    iVilotid visar rätt information för förare, företag och
                    kontroll.
                  </p>
                </div>
                <ul className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[#0B1120]/10 bg-[#0B1120]/10 md:grid-cols-5">
                  {authorityContexts.map((authority, index) => (
                    <li
                      key={authority.name}
                      className={`flex min-w-0 flex-col bg-white p-3 ${index === 4 ? 'col-span-2 md:col-span-1' : ''}`}
                    >
                      <div className="flex h-14 items-center justify-center rounded-lg border border-[#0B1120]/5 bg-[#FBFCFC] px-2">
                        <img
                          src={authority.logo}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="max-h-10 w-full object-contain"
                          onError={(event) => {
                            event.currentTarget.classList.add('hidden');
                            event.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <span className="hidden text-center text-xs font-bold leading-5 text-[#0B1120]">
                          {authority.name}
                        </span>
                      </div>
                      <p className="mt-2 text-center text-xs font-bold leading-5 text-[#0B1120]">
                        {authority.name}
                      </p>
                      <p className="mt-0.5 text-center text-xs leading-5 text-slate-700">
                        {authority.focus}
                      </p>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm leading-6 text-slate-700">
                  Myndigheterna visas som aktörer i taxibranschen. Det betyder
                  inte partnerskap, integration eller utbyte av data.
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#071A28] text-slate-300">
        <div className="mx-auto max-w-6xl px-5 py-9 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-7 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-sm">
              <BrandMark inverted compact />
              <p className="mt-4 text-sm leading-6 text-slate-300">
                Byggd för svensk taxi enligt Förordning (1994:1297) om vilotider
                och TSFS 2015:17 om personlig tidbok. En trygg plattform för
                förare, företag och myndighet.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="text-sm font-semibold text-white">Företag</h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-300">
                  <li>iVilotid AB</li>
                  <li>Stockholm, Sverige</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Kontakt</h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-300">
                  <li>
                    <a href="mailto:kontakt@ivilotid.se" className="hover:text-white">
                      kontakt@ivilotid.se
                    </a>
                  </li>
                  <li>
                    <a href="mailto:support@ivilotid.se" className="hover:text-white">
                      support@ivilotid.se
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-5 text-sm text-slate-300 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © {new Date().getFullYear()} iVilotid AB. Alla rättigheter
              förbehållna.
            </p>
            <p className="flex gap-5">
              <span>Integritetspolicy</span>
              <span>Användarvillkor</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
